// Local connects OAuth-style through the reverse proxy (self-signed cert) via
// the `mcp-remote` bridge: it opens a browser to authorize — no token. The proxy
// host must match the issuer/resource the server advertises (`https://localhost:8443`).
const LOCAL_PROXY_ORIGIN = "https://localhost:8443";
const LOCAL_HOSTS = ["localhost", "127.0.0.1", "0.0.0.0", "[::1]"];

/** Highlighted in the rendered snippet; the literal copied to the clipboard.
 *  Only the Python (SDK/REST) path uses a token — MCP clients authorize via OAuth. */
export const TOKEN_PLACEHOLDER = "<paste your token>";

/** Local connects to the MCP server through the dev reverse proxy and authorizes
 *  via OAuth in the browser (no PAT). Keyed off the host (not `import.meta.env.DEV`)
 *  so a local *production* build is still detected as local. Prod authorizes over
 *  HTTPS the same way, but native clients can skip the bridge. */
export const isLocalEnv = (): boolean =>
  LOCAL_HOSTS.includes(window.location.hostname);

// Connections are always org-gated (S8): every advertised URL is the org
// alias, whose OAuth consent is locked to that org.
const mcpPath = (orgId: string): string => `/org/${orgId}/mcp`;

export const mcpServerUrl = (orgId: string): string =>
  isLocalEnv()
    ? `${LOCAL_PROXY_ORIGIN}${mcpPath(orgId)}`
    : `${window.location.origin}${mcpPath(orgId)}`;

// Shared `mcp-remote` bridge entry for config-file clients (Claude Desktop, Cursor,
// VS Code, …). TLS verification is disabled for the dev proxy's self-signed cert.
const localBridgeServer = (orgId: string) => ({
  command: "npx",
  args: ["-y", "mcp-remote@latest", `${LOCAL_PROXY_ORIGIN}${mcpPath(orgId)}`],
  env: { NODE_TLS_REJECT_UNAUTHORIZED: "0" },
});

/** Local and prod register under different keys so one client config can hold
 *  both without the local bridge overwriting the hosted server; the org short-id
 *  keeps two orgs' connectors from overwriting each other the same way. */
export const serverName = (orgId: string): string => {
  // LAST 8 chars: org ids are UUIDv7, whose leading chars are a millisecond
  // timestamp — identical for orgs minted within ~65s (e.g. personal +
  // company org in one signup). The tail is the random part.
  const short = orgId.replace(/^org-/, "").slice(-8);
  return isLocalEnv() ? `mycorr-local-${short}` : `mycorr-${short}`;
};

/// The named entry on its own, dedented — what you paste *into* a config that
/// already has an `mcpServers` (or VS Code `servers`) object. Emitting the whole
/// wrapper invites replacing a file that already has other servers in it.
const configEntry = (orgId: string, entry: object): string =>
  JSON.stringify({ [serverName(orgId)]: entry }, null, 2)
    .split("\n")
    .slice(1, -1)
    .map((line) => line.slice(2))
    .join("\n");

const utf8ToBase64 = (s: string): string =>
  btoa(unescape(encodeURIComponent(s)));

export const cursorDeepLink = (orgId: string): string => {
  const config = utf8ToBase64(JSON.stringify({ url: mcpServerUrl(orgId) }));
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${serverName(orgId)}&config=${config}`;
};

export const vscodeDeepLink = (orgId: string): string => {
  const obj = {
    name: serverName(orgId),
    type: "http",
    url: mcpServerUrl(orgId),
  };
  return `vscode:mcp/install?${encodeURIComponent(JSON.stringify(obj))}`;
};

export const claudeCodeCmd = (orgId: string): string =>
  isLocalEnv()
    ? `claude mcp add ${serverName(orgId)} --env NODE_TLS_REJECT_UNAUTHORIZED=0 -- npx -y mcp-remote@latest ${mcpServerUrl(orgId)}`
    : `claude mcp add --transport http ${serverName(orgId)} ${mcpServerUrl(orgId)}`;

export const claudeDesktopEntry = (orgId: string): string =>
  configEntry(orgId, localBridgeServer(orgId));

export const codexCmd = (orgId: string): string =>
  isLocalEnv()
    ? `codex mcp add ${serverName(orgId)} --env NODE_TLS_REJECT_UNAUTHORIZED=0 -- npx -y mcp-remote@latest ${mcpServerUrl(orgId)}`
    : `codex mcp add ${serverName(orgId)} --url ${mcpServerUrl(orgId)}`;

export const mcpServerEntry = (orgId: string): string =>
  configEntry(
    orgId,
    isLocalEnv() ? localBridgeServer(orgId) : { url: mcpServerUrl(orgId) },
  );

export const vscodeServerEntry = (orgId: string): string =>
  configEntry(
    orgId,
    isLocalEnv()
      ? localBridgeServer(orgId)
      : { type: "http", url: mcpServerUrl(orgId) },
  );
